import React, { useMemo, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Autocomplete, Chip, CircularProgress, Divider, TextField, Tooltip } from '@mui/material';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import {
  getSingleFilteredOption,
  handleSingleOptionEnterToggle,
} from 'feature/loading-management/utils/autocompleteEnterToggle';

const INSURANCE_OPTIONS = ['All', 'Current', 'Expiring', 'Expired'];

const iconStyles = { position: 'absolute', right: 12, top: 14 };

const multiSelectCompactSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    flexWrap: 'nowrap',
    minHeight: 48,
  },
};

const renderTags = value => {
  const first = value[0];
  const rest = value.length - 1;
  return (
    <>
      <span
        style={{
          display: 'inline-block',
          maxWidth: 180,
          overflow: 'hidden',
          padding: '5px',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {first.label}
      </span>
      {rest > 0 && <Chip label={`+${rest}`} size="small" variant="autocomplete" />}
    </>
  );
};

const renderInputWithSpinner = (label, loading, optionsLength) => params =>
  (
    <TextField
      {...params}
      label={label}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
              ) : !loading && !optionsLength ? (
                <Tooltip title="No options available">
                  <InfoRoundedIcon sx={{ color: 'graphic.main', ...iconStyles }} />
                </Tooltip>
              ) : null}
              {!!optionsLength && params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  );

const getOptionLabel = option => option?.label || '';
const isCarrierOptionEqual = (option, val) =>
  (option?.carrierId ?? option?.value) === (val?.carrierId ?? val?.value);
const isLocationOptionEqual = (option, val) =>
  (option?.locationId ?? option?.value) === (val?.locationId ?? val?.value);

const CarrierAuthorizationsFilters = ({
  affiliateOptions,
  carrierOptions,
  carriersLoading,
  disabled,
  insuranceExpiration,
  locationOptions,
  locationsLoading,
  onAffiliateChange,
  onCarrierChange,
  onInsuranceExpirationChange,
  onLocationChange,
  selectedAffiliate,
  selectedCarriers,
  selectedLocations,
  showAffiliateFilter,
}) => {
  const [carrierInputValue, setCarrierInputValue] = useState('');
  const [locationInputValue, setLocationInputValue] = useState('');

  const singleFilteredCarrierOption = useMemo(
    () => getSingleFilteredOption(carrierOptions, carrierInputValue, getOptionLabel),
    [carrierInputValue, carrierOptions]
  );

  const singleFilteredLocationOption = useMemo(
    () => getSingleFilteredOption(locationOptions, locationInputValue, getOptionLabel),
    [locationInputValue, locationOptions]
  );

  return (
    <>
      {/* Affiliate — single-select, hidden when only 1 affiliate */}
      {showAffiliateFilter && (
        <Autocomplete
          autoHighlight
          disableClearable
          disabled={disabled}
          disablePortal
          getOptionLabel={option => option.label || ''}
          id="carrier-auth-affiliate-filter"
          isOptionEqualToValue={(option, val) => option.value === val?.value}
          onChange={(_, newValue) => onAffiliateChange(newValue)}
          options={affiliateOptions}
          renderInput={params => <TextField {...params} label="Affiliate" />}
          value={selectedAffiliate}
        />
      )}
      {showAffiliateFilter && <Divider />}
      {/* Carrier — multi-select */}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disabled={disabled}
        disablePortal
        getOptionLabel={getOptionLabel}
        id="carrier-auth-carrier-filter"
        inputValue={carrierInputValue}
        isOptionEqualToValue={isCarrierOptionEqual}
        loading={carriersLoading}
        multiple
        onChange={(_, newValue) => onCarrierChange(newValue)}
        onInputChange={(_, newInputValue) => setCarrierInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            singleFilteredCarrierOption,
            selectedCarriers,
            onCarrierChange,
            isCarrierOptionEqual
          )
        }
        openOnFocus
        options={carrierOptions}
        renderInput={renderInputWithSpinner('Carriers', carriersLoading, carrierOptions.length)}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={option.carrierId ?? option.value}>
            {option.label}
          </li>
        )}
        renderTags={renderTags}
        sx={multiSelectCompactSx}
        value={selectedCarriers}
      />
      {/* Location — multi-select */}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disabled={disabled}
        disablePortal
        getOptionLabel={getOptionLabel}
        id="carrier-auth-location-filter"
        inputValue={locationInputValue}
        isOptionEqualToValue={isLocationOptionEqual}
        loading={locationsLoading}
        multiple
        onChange={(_, newValue) => onLocationChange(newValue)}
        onInputChange={(_, newInputValue) => setLocationInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            singleFilteredLocationOption,
            selectedLocations,
            onLocationChange,
            isLocationOptionEqual
          )
        }
        openOnFocus
        options={locationOptions}
        renderInput={renderInputWithSpinner('Locations', locationsLoading, locationOptions.length)}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={option.locationId ?? option.value}>
            {option.label}
          </li>
        )}
        renderTags={renderTags}
        sx={multiSelectCompactSx}
        value={selectedLocations}
      />
      {/* Insurance Expiration */}
      <Autocomplete
        autoHighlight
        disableClearable
        disabled={disabled}
        disablePortal
        id="carrier-auth-insurance-expiration-filter"
        onChange={(_, newValue) => onInsuranceExpirationChange(newValue || 'All')}
        options={INSURANCE_OPTIONS}
        renderInput={params => <TextField {...params} label="Insurance Expiration" />}
        value={insuranceExpiration}
      />
    </>
  );
};

CarrierAuthorizationsFilters.propTypes = {
  affiliateOptions: arrayOf(shape({ label: string, value: string })),
  carrierOptions: arrayOf(shape({ label: string })),
  carriersLoading: bool,
  disabled: bool,
  insuranceExpiration: string,
  locationOptions: arrayOf(shape({ label: string })),
  locationsLoading: bool,
  onAffiliateChange: func.isRequired,
  onCarrierChange: func.isRequired,
  onInsuranceExpirationChange: func.isRequired,
  onLocationChange: func.isRequired,
  selectedAffiliate: shape({ label: string, value: string }),
  selectedCarriers: arrayOf(shape({ label: string })),
  selectedLocations: arrayOf(shape({ label: string })),
  showAffiliateFilter: bool,
};

CarrierAuthorizationsFilters.defaultProps = {
  affiliateOptions: [],
  carrierOptions: [],
  carriersLoading: false,
  disabled: false,
  insuranceExpiration: 'All',
  locationOptions: [],
  locationsLoading: false,
  selectedAffiliate: null,
  selectedCarriers: [],
  selectedLocations: [],
  showAffiliateFilter: true,
};

export default CarrierAuthorizationsFilters;
